class FsrFreeGifts extends HTMLElement {
  constructor() {
    super();
    this.boundHandleCartChange = this._handleCartChange.bind(this);
  }

  connectedCallback() {
    this._attachSelectors();
    if (!window.listenersAttached) {
      this._attachListeners();
      window.listenersAttached = true;
    }
    this._updateProgressBar();
    this._updateFreeGiftText();
  }

  _attachSelectors() {
    this.cartTotal = this.getAttribute("cart-total");
    this.thresholdFreeShipping = this.getAttribute("threshold_one");
    this.thresholdTwo = this.getAttribute("threshold_two");
    this.thresholdThree = this.getAttribute("threshold_three");
    this.freeGiftIdOne = this.getAttribute("free-gift-id-1");
    this.freeGiftIdTwo = this.getAttribute("free-gift-id-2");
    this.progressBar = document.querySelector(".free-gift-progress");
    this.freeGiftText = document.querySelector(".free-gift-text");
  }

  _attachListeners() {
    document.documentElement.addEventListener(
      "cart:change",
      this.boundHandleCartChange
    );
    document.documentElement.addEventListener(
      "fsr:cart:updated",
      this.boundHandleCartChange
    );
  }

  _removeListeners() {
    document.documentElement.removeEventListener(
      "fsr:cart:updated",
      this.boundHandleCartChange
    );
  }

  _handleCartChange(event) {
    const cart = event.detail.cart;
    this.cartTotal = cart.total_price;
    this._updateCart(this.cartTotal);
    this._removeListeners();
  }

  _updateCart(cartTotal) {
    if (cartTotal >= parseFloat(this.thresholdThree)) {
      this._onSubmit([this.freeGiftIdOne, this.freeGiftIdTwo]);
    } else if (cartTotal >= parseFloat(this.thresholdTwo)) {
      this._onSubmit([this.freeGiftIdOne]);
      this._onRemove([this.freeGiftIdTwo]).then();
    } else {
      this._onRemove([this.freeGiftIdOne, this.freeGiftIdTwo]).then();
    }
  }

  _onSubmit(giftIds) {
    if (
      giftIds.every((giftId) =>
        window.localStorage.getItem(`gift-${giftId}-added`)
      )
    )
      return;
    const giftIdsToAdd = giftIds.filter(
      (giftId) =>
        !window.localStorage.getItem(`gift-${giftId}-added`) &&
        !window.localStorage.getItem(`gift-${giftId}-add-in-progress`)
    );

    let sectionsToBundle = ["variant-added"];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", {
        bubbles: true,
        detail: { sections: sectionsToBundle },
      })
    );
    const formData = new FormData();
    formData.set("sections", sectionsToBundle.join(","));

    let i = 0;
    for (let giftId of giftIdsToAdd) {
      window.localStorage.setItem(`gift-${giftId}-add-in-progress`, "y");
      formData.append(`items[${i}][id]`, giftId);
      formData.append(`items[${i}][quantity]`, "1");
      formData.append(`items[${i}]properties[_free-gift]`, "true");
      i++;
    }

    fetch("/cart/add.js", {
      body: formData,
      method: "POST",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
      },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        return response.json();
      })
      .then((cart) => {
        document.dispatchEvent(
          new CustomEvent("fsr:gift:added", {
            bubbles: true,
            detail: {
              cart: cart,
            },
          })
        );
        for (let giftId of giftIdsToAdd) {
          window.localStorage.setItem(`gift-${giftId}-add-in-progress`, "");
          window.localStorage.setItem(`gift-${giftId}-added`, "y");
        }
      })
      .catch((e) => {
        for (let giftId of giftIdsToAdd) {
          window.localStorage.setItem(`gift-${giftId}-add-in-progress`, "");
        }
        console.error(e);
      });
  }

  async _onRemove(giftIds) {
    if (
      giftIds.every(
        (giftId) => !window.localStorage.getItem(`gift-${giftId}-added`)
      )
    )
      return;

    const giftIdsToRemove = giftIds.filter(
      (giftId) =>
        window.localStorage.getItem(`gift-${giftId}-added`) &&
        !window.localStorage.getItem(`gift-${giftId}-remove-in-progress`)
    );

    for (let giftId of giftIdsToRemove) {
      let sectionsToBundle = ["variant-added"];
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:prepare-bundled-sections", {
          bubbles: true,
          detail: { sections: sectionsToBundle },
        })
      );
      const formData = new FormData();
      formData.set("sections", sectionsToBundle.join(","));
      formData.append("id", giftId);
      formData.append("quantity", "0");

      try {
        const response = await fetch("/cart/change.js", {
          body: formData,
          method: "POST",
          headers: {
            "X-Requested-With": "XMLHttpRequest",
          },
        });

        if (!response.ok) {
          throw new Error("Network response was not ok");
        }
        const cart = await response.json();

        document.dispatchEvent(
          new CustomEvent("fsr:gift:added", {
            bubbles: true,
            detail: {
              cart: cart,
            },
          })
        );

        for (let giftId of giftIdsToRemove) {
          window.localStorage.setItem(`gift-${giftId}-remove-in-progress`, "");
          window.localStorage.setItem(`gift-${giftId}-added`, "");
        }
      } catch (error) {
        for (let giftId of giftIdsToRemove) {
          window.localStorage.setItem(`gift-${giftId}-remove-in-progress`, "");
        }
        console.error("Error:", error);
      }
      await new Promise((resolve) => setTimeout(resolve, 500));
    }
  }

  _updateProgressBar() {
    const cartTotalCents = parseFloat(this.cartTotal);
    const thresholdOneCents = parseFloat(this.thresholdFreeShipping);
    const thresholdTwoCents = parseFloat(this.thresholdTwo);
    const thresholdThreeCents = parseFloat(this.thresholdThree);
    const freeGiftItems = document.querySelectorAll(".free-gift-item");

    let progressBarWidth;

    if (cartTotalCents < thresholdOneCents) {
      progressBarWidth = (cartTotalCents / thresholdOneCents) * 30;
      freeGiftItems.forEach((item) => (item.style.opacity = "0.5"));
    } else if (cartTotalCents < thresholdTwoCents) {
      const percentageBetweenThresholds =
        (cartTotalCents - thresholdOneCents) /
        (thresholdTwoCents - thresholdOneCents);
      progressBarWidth = 33 + percentageBetweenThresholds * (66 - 33);
      freeGiftItems.forEach(
        (item, index) => (item.style.opacity = index < 1 ? "1" : "0.5")
      );
    } else if (cartTotalCents < thresholdThreeCents) {
      const percentageBetweenThresholds =
        (cartTotalCents - thresholdTwoCents) /
        (thresholdThreeCents - thresholdTwoCents);
      progressBarWidth = 66 + percentageBetweenThresholds * (100 - 66);
      freeGiftItems.forEach(
        (item, index) => (item.style.opacity = index < 2 ? "1" : "0.5")
      );
    } else {
      progressBarWidth = 100;
      freeGiftItems.forEach((item) => (item.style.opacity = "1"));
    }

    this.progressBar.style.width = `${progressBarWidth}%`;
  }

  _updateFreeGiftText() {
    const cartTotalCents = parseFloat(this.cartTotal);
    let remainingAmount;
    let message;

    function formatPrice(amount) {
      return amount.toFixed(2).replace(".", ",");
    }

    if (cartTotalCents < parseFloat(this.thresholdFreeShipping)) {
      remainingAmount =
        (parseFloat(this.thresholdFreeShipping) - cartTotalCents) / 100;
      message = `Es fehlen noch ${formatPrice(
        remainingAmount
      )}€ für kostenlosen Versand!`;
    } else if (cartTotalCents < parseFloat(this.thresholdTwo)) {
      remainingAmount = (parseFloat(this.thresholdTwo) - cartTotalCents) / 100;
      message = `Es fehlen noch ${formatPrice(
        remainingAmount
      )}€ für eine gratis Saatgut-Box!`;
    } else if (cartTotalCents < parseFloat(this.thresholdThree)) {
      remainingAmount =
        (parseFloat(this.thresholdThree) - cartTotalCents) / 100;
      message = `Es fehlen noch ${formatPrice(
        remainingAmount
      )}€ für eine gratis Gießkanne!`;
    } else {
      message = "Alle Belohnungen freigeschaltet!";
    }

    this.freeGiftText.textContent = message;
  }
}

customElements.define("fsr-free-gifts", FsrFreeGifts);
